import { $ } from "kw/api";
import { deref } from "kanopi-components/utils";
import { QuoteFilterBy, RiskStateFilterEnum } from "kw/api/zeus";
import { substituteRiskData } from "kw/util/substituteRiskData";
import { useAPI } from "kw/api/useAPI";
import { useState } from "react";
import { exposureFields, riskStateQuestionSetFields } from "kw/api/fragments";
import { jsonStringToCamelCase } from "kanopi-components/utils/json";

export const useQuotes = () => {
  const [quotes, setQuotes] = useState<TAdminQuotes>();
  const [quote, setQuote] = useState<TQuote>();
  const [notes, setNotes] = useState<[TNote]>();
  const [loadingReprised, setLoadingReprised] = useState<boolean>(false);
  const { query, loading } = useAPI();
  const { mutation, loading: repricing } = useAPI();
  const fetchQuotes = (search = "", filterBy = QuoteFilterBy.ALL, page: number) => {
    query(
      quotesQuery,
      (response: { admin: { quotes: TAdminQuotes } }) => {
        setQuotes({
          ...response.admin.quotes,
          items: response.admin.quotes.items.map((quote) => ({
            ...quote,
            riskProfile: {
              ...quote?.riskProfile,
              riskData: jsonStringToCamelCase(quote?.riskProfile?.riskData),
            },
          })),
        });
      },
      {
        search,
        filterBy,
        page,
      }
    );
  };

  const searchQuotes = (search = "", filterBy = QuoteFilterBy.ALL, page = 1) => {
    query(
      quotesQuery,
      (response: { admin: { quotes: TAdminQuotes } }) => {
        setQuotes(response.admin.quotes);
      },
      {
        search: search,
        filterBy: filterBy,
        page: page,
      }
    );
  };
  const adjustQuote = (quote) => {
    mutation(
      {
        admin: {
          priceReferredRiskProfile: [
            {
              riskProfileId: quote?.riskProfile.id,
              checkReferred: false,
            },
            {
              id: true,
            },
          ],
        },
      },

      () => {
        // An unsuccessful response will generate an error handled by the API hook
        fetchQuote(quote.id);
      },
      undefined,
      undefined
    );
  };
  const extractQuestionSets = (
    quoteQueryResp: { admin: { quote: { details: TQuote; notes: [TNote] } } },
    questionSets: [TQuestionSet]
  ) => {
    let riskProfile = null;
    const qs = [];
    for (const questionSet of questionSets) {
      const [enquiry, originalRiskProfile] = substituteRiskData(
        {
          questionSet,
        },
        deref(quoteQueryResp.admin, "quote.details.riskProfile", {})
      );
      qs.push(enquiry.questionSet);
      riskProfile = originalRiskProfile;
    }

    if (!riskProfile) {
      riskProfile = deref(quoteQueryResp.admin, "quote.details.riskProfile", {});
    }
    return [riskProfile, qs];
  };

  const fetchQuote = (id: string, repriced = false) => {
    if (repriced) {
      setLoadingReprised(true);
    }
    query(
      quoteQuery,
      (response: { admin: { quote: { details: TQuote; notes: [TNote] } } }) => {
        let _quote = response.admin.quote.details;
        const riskStateQuestionSets = deref(
          response.admin,
          "quote.details.riskProfile.riskStateQuestionSets",
          []
        );
        const adjustmentQuestionSets = deref(
          response.admin,
          "quote.details.riskProfile.adjustmentQuestionSets",
          []
        );
        const knockoutQuestionSets = deref(
          response.admin,
          "quote.details.riskProfile.knockoutQuestionSets",
          []
        );
        const policyHolderQuestionSets = deref(
          response.admin,
          "quote.details.riskProfile.policyHolderQuestionSets",
          []
        );
        const [riskProfile, qs] = extractQuestionSets(response, riskStateQuestionSets);
        let finalRiskProfile = {
          ...riskProfile,
          riskStateQuestionSets: qs,
        };
        if (finalRiskProfile?.riskData?.adjustments) {
          const [, qs] = extractQuestionSets(response, adjustmentQuestionSets);
          finalRiskProfile = {
            ...finalRiskProfile,
            adjustmentQuestionSets: qs,
          };
        }
        if (finalRiskProfile?.riskData?.referrals) {
          const [, qs] = extractQuestionSets(response, knockoutQuestionSets);
          finalRiskProfile = {
            ...finalRiskProfile,
            knockoutQuestionSets: qs,
          };
        }
        if (finalRiskProfile?.riskData?.policyHolder) {
          const [, qs] = extractQuestionSets(response, policyHolderQuestionSets);
          finalRiskProfile = {
            ...finalRiskProfile,
            policyHolderQuestionSets: qs,
          };
        }
        if (_quote.pricingOthers) {
          _quote.pricingOthers = JSON.parse(_quote.pricingOthers);
        }
        _quote = {
          ...response.admin.quote.details,
          ...{ riskProfile: finalRiskProfile },
        };
        setQuote(_quote);
        if (repriced) {
          setLoadingReprised(false);
        }
        setNotes(response.admin.quote.notes);
      },
      {
        id,
        filterBy: RiskStateFilterEnum.ALL,
      }
    );
  };
  return {
    quotes,
    quote,
    notes,
    loading,
    repricing,
    loadingReprised,
    fetchQuotes,
    fetchQuote,
    adjustQuote,
    searchQuotes,
  };
};

const quotesQuery = {
  admin: {
    quotes: [
      {
        search: $("search", "String!"),
        filterBy: $("filterBy", "QuoteFilterBy!"),
        page: $("page", "Int!"),
      },
      {
        totalItemCount: true,
        page: true,
        pageCount: true,
        pageSize: true,
        hasNext: true,
        hasPrev: true,
        items: {
          id: true,
          riskProfile: {
            owner: {
              firstName: true,
              lastName: true,
              isActive: true,
            },
            riskData: true,
          },
          startDate: true,
          finishDate: true,
          postTaxPremium: true,
          state: true,
          stateDisplay: true,
          referralState: true,
          referralStateDisplay: true,
        },
      },
    ],
  },
};

const quoteQuery = {
  admin: {
    quote: [
      {
        id: $("id", "ID!"),
      },
      {
        details: {
          id: true,
          startDate: true,
          finishDate: true,
          createdTimestamp: true,
          updatedTimestamp: true,
          covers: {
            key: true,
            title: true,
            description: true,
          },
          documents: {
            name: true,
            type: true,
            url: true,
            created: true,
          },
          pricingOthers: true, // Provide an initializer for the pricingOthers property
          riskProfile: {
            id: true,
            owner: {
              id: true,
              firstName: true,
              lastName: true,
              contactEmail: true,
              isActive: true,
            },
            riskData: true,
            enabledExposures: true,
            riskStateQuestionSets: [
              {
                filterBy: $("filterBy", "RiskStateFilterEnum!"),
              },
              {
                ...riskStateQuestionSetFields,
              },
            ],
            __alias: {
              adjustmentQuestionSets: {
                riskStateQuestionSets: [
                  {
                    filterBy: RiskStateFilterEnum.ADJUSTMENT,
                  },
                  {
                    ...riskStateQuestionSetFields,
                  },
                ],
              },
              exposureQuestionSets: {
                riskStateQuestionSets: [
                  {
                    filterBy: RiskStateFilterEnum.EXPOSURE,
                  },
                  {
                    ...riskStateQuestionSetFields,
                  },
                ],
              },
              knockoutQuestionSets: {
                riskStateQuestionSets: [
                  {
                    filterBy: RiskStateFilterEnum.KNOCKOUT,
                  },
                  {
                    ...riskStateQuestionSetFields,
                  },
                ],
              },
              policyHolderQuestionSets: {
                riskStateQuestionSets: [
                  {
                    filterBy: RiskStateFilterEnum.POLICY_HOLDER,
                  },
                  {
                    ...riskStateQuestionSetFields,
                  },
                ],
              },
            },
          },
          paymentFrequency: true,
          preTaxPremium: true,
          postTaxPremium: true,
          unadjustedPostTaxPremium: true,
          periodicPrice: true,
          amountDue: true,
          state: true,
          stateDisplay: true,
          referralState: true,
          referralStateDisplay: true,
          referredQuestionSetKeys: true,
          commission: true,
          messages: true,
          privateMessages: true,
          fees: {
            breakdown: {
              feeType: {
                key: true,
                label: true,
              },
              amount: true,
            },
          },
          product: {
            name: true,
            key: true,
            provider: {
              name: true,
            },
            availableCovers: {
              key: true,
              title: true,
              description: true,
            },
            exposures: {
              ...exposureFields,
            },
          },
          policyPeriod: {
            documents: {
              name: true,
              type: true,
              url: true,
              created: true,
            },
          },
        },
        notes: [
          {},
          {
            id: true,
            content: true,
            author: {
              id: true,
              firstName: true,
              lastName: true,
            },
            created: true,
          },
        ],
      },
    ],
  },
};
